import { cloneDeep } from 'lodash'
import Vue from 'vue'
import moment from 'moment'
import { wait } from '@modules/time'
import { showAncestors } from '@modules/filters'

const state = {
  closeDropdowns: false,
  search: {
    value: '',
    category: ''
  },
  categories: [],
  selected: [],
  activeFilters: {
    filters: [],
    date: {},
    search: {}
  },
  date: {
    start: '',
    end: ''
  },
  icons: {
    company: 'briefcase',
    groupsTeams: 'workgroups',
    workArea: 'target',
    roleType: 'pencil',
    meetingType: 'calendar',
    document: 'briefcase',
    topics: 'topics',
    subtopics: 'subtopics',
    myDocument: 'user',
    date: 'user'
  }
}

const getters = {
  getCategories: state => state.categories,
  getIcons: state => state.icons,
  getSelected: state => state.selected,
  getSearch: state => state.search,
  getDate: state => state.date,
  getStartDate: state => state.date.start,
  getEndDate: state => state.date.end,
  getActiveFilters: state => state.activeFilters
}

const mutations = {
  UPDATE_CLOSE_DROPDOWNS: (state, boolean) => {
    state.closeDropdowns = boolean
  },
  UPDATE_CATEGORIES: (state, array) => {
    state.categories = [ ...array ]
  },
  UPDATE_SELECTED: (state, array) => {
    state.selected = [ ...array ]
  },
  UPDATE_SEARCH: (state, object) => {
    const merged = Object.assign({ ...state.search }, object)
    state.search = merged
  },
  UPDATE_START_DATE: (state, string) => {
    Vue.set(state.date, 'start', string)
  },
  UPDATE_END_DATE: (state, string) => {
    Vue.set(state.date, 'end', string)
  },
  UPDATE_ACTIVE_FILTERS: state => {
    Vue.set(state.activeFilters, 'filters', cloneDeep(state.selected))
    Vue.set(state.activeFilters, 'date', {
      start: state.date.start ? moment(state.date.start).format('DD/MM/YYYY') : '',
      end: state.date.end ? moment(state.date.end).format('DD/MM/YYYY') : ''
    })
    Vue.set(state.activeFilters, 'search', state.search)
  },
  UPDATE_ITEM_ACTIVE: (state, { categoryIndex, guid, active }) => {
    const items = state.categories[categoryIndex].items
    const index = items.findIndex(item => item.guid === guid)
    if (index > -1) {
      const item = items[index]
      // if (item.parent === '') {
      //   items.forEach(item => {
      //     if (item.parent === guid) Vue.set(item, 'active', active)
      //   })
      // }
      Vue.set(item, 'active', active)
    } else {
      console.error(`UPDATE_ITEM_ACTIVE : Index not found || ${guid}`)
    }
  },
  UPDATE_ITEM_SHOW: (state, { categoryIndex, guid, status }) => {
    const items = state.categories[categoryIndex].items
    const index = items.findIndex(item => item.guid === guid)
    if (index > -1) {
      Vue.set(items[index], 'show', status)
    } else {
      console.error(`UPDATE_ITEM_SHOW : Index not found || ${guid}`)
    }
  }
}

const actions = {
  updateCategories: ({ commit, dispatch }, categories) => {
    commit('UPDATE_CATEGORIES', categories)
    dispatch('updateSelected')
  },
  updateItemActive: ({ commit }, { categoryIndex, guid, active }) => {
    commit('UPDATE_ITEM_ACTIVE', { categoryIndex, guid, active })
  },
  updateItemShow: ({ commit }, { categoryIndex, guid, status }) => {
    commit('UPDATE_ITEM_SHOW', { categoryIndex, guid, status })
  },
  updateAllItemsActive: ({ dispatch, state }, { categoryIndex, active }) => {
    state.categories[categoryIndex].items.forEach(item => {
      dispatch('updateItemActive', {
        categoryIndex,
        guid: item.guid,
        active: active
      })
    })
  },
  updateAllItemsShow: ({ dispatch, state }, { categoryIndex, query }) => {
    const items = state.categories[categoryIndex].items
    items.forEach(item => {
      const status = item.label.toLowerCase().includes(query.toLowerCase())
      dispatch('updateItemShow', { categoryIndex, guid: item.guid, status })
      if (status) showAncestors({ dispatch, categoryIndex, items, item })
    })
  },
  updateSelected: ({ commit, state }) => {
    const selected = []
    const categories = cloneDeep(state.categories)
    categories.forEach(category => {
      const items = cloneDeep(category.items)
      const returnedCategory = {
        guid: category.guid,
        items: []
      }
      items.forEach(item => {
        if (item.active) returnedCategory.items.push(item)
      })
      selected.push(returnedCategory)
    })
    commit('UPDATE_SELECTED', selected)
    commit('UPDATE_ACTIVE_FILTERS')
  },
  // Activates the items matching the guids passed through the `filters` query
  // string (e.g. /contacts/list?filters=group-item-15). Has to run once the
  // categories of the page are loaded and before the list itself is fetched,
  // otherwise the request goes out without the filters of the url.
  applyUrlFilters: ({ dispatch, state }, filters) => {
    const guids = (Array.isArray(filters) ? filters : String(filters || '').split(','))
      .map(guid => guid.trim())
      .filter(guid => guid !== '')

    if (guids.length) {
      state.categories.forEach((category, categoryIndex) => {
        category.items.forEach(item => {
          if (guids.indexOf(item.guid) > -1 && !item.active) {
            dispatch('updateItemActive', { categoryIndex, guid: item.guid, active: true })
          }
        })
      })
    }

    dispatch('updateSelected')
  },
  updateSearch: ({ commit }, search) => {
    commit('UPDATE_SEARCH', search)
    commit('UPDATE_ACTIVE_FILTERS')
  },
  updateStartDate: ({ commit }, start) => {
    commit('UPDATE_START_DATE', start)
    commit('UPDATE_ACTIVE_FILTERS')
  },
  updateEndDate: ({ commit }, end) => {
    commit('UPDATE_END_DATE', end)
    commit('UPDATE_ACTIVE_FILTERS')
  },
  updateCloseDropdowns: ({ commit }, status) => {
    commit('UPDATE_CLOSE_DROPDOWNS', status)
  },
  reset: ({ state, dispatch }) => {
    dispatch('updateSearch', { value: '', category: '' })
    dispatch('updateStartDate', '')
    dispatch('updateEndDate', '')
    state.categories.forEach((category, index) => {
      dispatch('updateAllItemsActive', { categoryIndex: index, active: false })
    })
    dispatch('updateSelected')
  },
  fetchData: ({ state, dispatch, commit }, { route, context }) => {
    dispatch('updateCloseDropdowns', true)
    switch (route) {
      case 'Contacts List':
        dispatch('page/fetchGetUsers', state.activeFilters, { root: true })
        break
      case 'Search Results':
        dispatch('page/fetchGetSearch', state.activeFilters, { root: true })
        break
      case 'Calendar View':
        if (context === 'filters') {
          if (state.activeFilters.date.start !== '') {
            dispatch('page/fetchGetCalendarEvents', state.activeFilters, { root: true })
          }
        }
        break
      case 'Agenda List View':
        dispatch('page/fetchGetEvents', state.activeFilters, { root: true })
        break
      case 'Agenda Table View':
        dispatch('page/fetchGetEvents', state.activeFilters, { root: true })
        break
      case 'Documents List':
        dispatch('page/fetchGetDocuments', state.activeFilters, { root: true })
        break
    }

    wait(300, () => {
      dispatch('updateCloseDropdowns', false)
    })
  }
}

export default {
  state,
  getters,
  mutations,
  actions,
  namespaced: true
}
